import React from 'react';
import { Box, Divider, FormControlLabel, Switch, TextField } from '@mui/material';
import { Dispatch } from '@reduxjs/toolkit';
import { setSettings } from '../../../../support/slices/tileBuilderSlice';
import { TileSettings } from '../../../../types/tileBuilderTypes';
import { ensureNumber } from '../../../../support/utils';
import ChartValueScaleControls from './ChartValueScaleControls';

interface AxesGridControlsProps {
  chartId: string;
  settings: TileSettings;
  dispatch: Dispatch;
  chartKind: string;
  isLineChart: boolean;
}

const AxesGridControls: React.FC<AxesGridControlsProps> = ({
                                                             chartId,
                                                             settings,
                                                             dispatch,
                                                             chartKind,
                                                             isLineChart,
                                                           }) => {
  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap={2}>
        <TextField
          label="X Axis Label"
          value={settings.xAxisLabel ?? ''}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { xAxisLabel: e.target.value },
              })
            )
          }
          sx={{ minWidth: 200 }}
        />

        <TextField
          label="Y Axis Label"
          value={settings.yAxisLabel ?? ''}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { yAxisLabel: e.target.value },
              })
            )
          }
          sx={{ minWidth: 200 }}
        />

        <TextField
          label="Axis Bottom Legend"
          value={settings.axisBottomLegend ?? ''}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { axisBottomLegend: e.target.value },
              })
            )
          }
          sx={{ minWidth: 220 }}
        />

        <TextField
          label="Axis Bottom Legend Offset"
          type="number"
          value={settings.axisBottomLegendOffset ?? 40}
          onChange={(e) => {
            const nextOffset = Number(e.target.value);

            dispatch(
              setSettings({
                chartId,
                settings: { axisBottomLegendOffset: nextOffset },
              })
            );

            const absOffset = Math.abs(nextOffset);
            const currentBottom = ensureNumber(settings.marginBottom, 48);
            const requiredBottom = Math.max(48, absOffset + 18);

            if (currentBottom < requiredBottom) {
              dispatch(
                setSettings({
                  chartId,
                  settings: { marginBottom: requiredBottom },
                })
              );
            }
          }}
          sx={{ width: 220 }}
          helperText="Large offsets require larger bottom margin"
        />

        <TextField
          label="Axis Bottom Rotation"
          type="number"
          value={settings.axisBottomRotation ?? 0}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { axisBottomRotation: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 220 }}
        />

        <TextField
          label="Axis Left Legend"
          value={settings.axisLeftLegend ?? ''}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { axisLeftLegend: e.target.value },
              })
            )
          }
          sx={{ minWidth: 220 }}
        />

        <TextField
          label="Axis Left Legend Offset"
          type="number"
          value={settings.axisLeftLegendOffset ?? -55}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { axisLeftLegendOffset: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 220 }}
        />

        <TextField
          label="Axis Left Rotation"
          type="number"
          value={settings.axisLeftRotation ?? 0}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { axisLeftRotation: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 220 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={!!settings.enableGridX}
              onChange={(e) =>
                dispatch(
                  setSettings({
                    chartId,
                    settings: { enableGridX: e.target.checked },
                  })
                )
              }
            />
          }
          label="Enable Grid X"
        />

        <FormControlLabel
          control={
            <Switch
              checked={settings.enableGridY !== false}
              onChange={(e) =>
                dispatch(
                  setSettings({
                    chartId,
                    settings: { enableGridY: e.target.checked },
                  })
                )
              }
            />
          }
          label="Enable Grid Y"
        />
      </Box>

      {isLineChart && (
        <>
          <Divider sx={{ my: 2 }} />
          <ChartValueScaleControls
            chartId={chartId}
            settings={settings}
            dispatch={dispatch}
            chartKind={chartKind}
            isLineChart={isLineChart}
          />
        </>
      )}
    </Box>
  );
};

export default AxesGridControls;